import {
	createActionsHook,
	createHook,
	createStore,
	defaultRegistry,
	type BoundActions,
	type HookActionsFunction,
	type HookFunction,
	type StoreActionApi,
} from 'react-sweet-state';

import type {
	ApiTask,
	ApiTaskSSEEventType,
	ApiTaskSSEUpdate,
	ApiTaskSnapshotSSEUpdate,
	ApiTaskStatus,
	AssistanceService,
	ConversationChannelTask,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { subscribeToTasksSSE } from '@atlassian/conversation-assistant-service/services/tasks-sse/tasks-sse-connection';

import type { ExecutionSegment, TaskProgressState, TodoSnapshot } from '../chat/types';
import {
	mapApiTaskStatusToProgressState,
	mergeExecutionSegments,
} from '../tasks-shared/task-helpers';
import { selectTaskNotices, type TaskNotice } from './selectTaskNotices';

// ─── Types ────────────────────────────────────────────────────────────────────

export type UnifiedTask = {
	taskId: string;
	conversationId: string | null;
	agentId: string | null;
	triggerType: 'EXPLICIT' | 'IMPLICIT' | null;

	progressState: TaskProgressState;
	rawStatus: ApiTaskStatus;

	/**
	 * Invocation id of the action currently awaiting a user response (the
	 * `ask_question` / plan-confirmation prompt). Persisted here — rather than a
	 * component-local ref — so the input overlay can re-derive the pending prompt
	 * after a remount (e.g. mini-modal ↔ sidebar mode switch). `null` when nothing
	 * is awaiting a response.
	 */
	activeInvocationId: string | null;

	title: string;
	plan: { version: number; text: string } | null;
	resultSummary: string | null;
	errorMessage: string | null;

	todoSnapshot: TodoSnapshot | undefined;
	currentStep: ApiTaskSSEUpdate['current_step'];
	executionSegments: ExecutionSegment[] | null;

	messageId: string | null;
	completionMessageId: string | null;
	currentMessageId: string | null;

	/** Local-only UI state — NEVER overwritten by API/SSE responses. */
	seekingCompletedAck: boolean;

	createdAt: string | null;
	startedAt: string | null;
	updatedAt: string;
	completedAt: string | null;
};

export type TasksStoreState = {
	/** Single source of truth for all task data, keyed by taskId. */
	tasksByTaskId: Map<string, UnifiedTask>;

	/** Opaque cursor for the next page of the all-tasks REST list. */
	allTasksNextCursor: string | null;
	/** True after at least one all-tasks REST page has populated the cursor metadata. */
	hasLoadedAllTasksPage: boolean;

	/**
	 * Active conversation context used to guard against stale SSE reconnect
	 * replays overwriting in-flight stream state.
	 * Populated by useHydrateTaskStoreContext.
	 */
	activeConversationId: string | null;
	activeConversationLastAgentMessageId: string | null;
	activeConversationStatus: number | null; // CHAT_STORE_STATUS enum value

	/** SSE ref-counting for subscriber lifecycle management. */
	sseSubscriberCount: number;
	sseConnected: boolean;
};

const initialState: TasksStoreState = {
	tasksByTaskId: new Map(),
	allTasksNextCursor: null,
	hasLoadedAllTasksPage: false,
	activeConversationId: null,
	activeConversationLastAgentMessageId: null,
	activeConversationStatus: null,
	sseSubscriberCount: 0,
	sseConnected: false,
};

// ─── Terminal status constants ────────────────────────────────────────────────

const TERMINAL_STATUSES: ReadonlySet<ApiTaskStatus> = new Set<ApiTaskStatus>([
	'COMPLETED',
	'FAILED',
	'CANCELLED',
]);

const TERMINAL_PROGRESS_STATES: ReadonlySet<TaskProgressState> = new Set<TaskProgressState>([
	'Completed',
	'Failed',
	'Cancelled',
]);

const isTerminalStatus = (status: ApiTaskStatus): boolean => TERMINAL_STATUSES.has(status);

const isTerminalProgressState = (state: TaskProgressState): boolean =>
	TERMINAL_PROGRESS_STATES.has(state);

/**
 * Progress states that carry a pending-prompt `activeInvocationId`. The id is
 * only meaningful while the task stays in one of these states; once it leaves
 * (to RUNNING, a terminal status, or a *different* awaiting state) the prompt is
 * gone and the id must be cleared so a dead invocation can't later rebind an
 * overlay on a fresh mount.
 */
const INVOCATION_BEARING_PROGRESS_STATES: ReadonlySet<TaskProgressState> =
	new Set<TaskProgressState>(['AwaitingDisambiguation', 'AwaitingPlanConfirmation']);

const shouldPreserveActiveInvocationId = (
	existing: UnifiedTask | undefined,
	resolvedProgressState: TaskProgressState,
): boolean =>
	!!existing &&
	existing.progressState === resolvedProgressState &&
	INVOCATION_BEARING_PROGRESS_STATES.has(resolvedProgressState);

// ─── Status mapping ───────────────────────────────────────────────────────────
// Sourced from the shared `tasks-shared/task-helpers` module.

// ─── Merge helpers ────────────────────────────────────────────────────────────

/**
 * Returns true if `incoming` should overwrite `existing` for the same taskId.
 *
 * Monotonic rules:
 *   1. No existing → always accept incoming.
 *   2. Existing is terminal → only accept a strictly newer terminal update.
 *   3. Otherwise → accept if incoming.updated_at >= existing.updated_at.
 */
function shouldOverwrite(
	existing: UnifiedTask | undefined,
	incomingUpdatedAt: string,
	incomingStatus: ApiTaskStatus,
): boolean {
	if (!existing) {
		return true;
	}
	if (isTerminalStatus(existing.rawStatus)) {
		return isTerminalStatus(incomingStatus) && incomingUpdatedAt > existing.updatedAt;
	}
	return incomingUpdatedAt >= existing.updatedAt;
}

// `mergeExecutionSegments` is imported from `../tasks-shared/task-helpers` above.

/**
 * Parses the raw execution_segments Record from the API into ExecutionSegment[].
 */
export function parseExecutionSegments(
	raw: Record<string, string> | null | undefined,
): ExecutionSegment[] | null {
	if (!raw) {
		return null;
	}
	try {
		return Object.values(raw).map((value) => {
			const parsed = JSON.parse(value);
			return {
				startedAt: parsed.startedAt,
				...(parsed.endedAt ? { endedAt: parsed.endedAt } : {}),
			};
		});
	} catch {
		return null;
	}
}

/**
 * Parses the raw todo_list Record from the API into a TodoSnapshot.
 */
export function parseTodoList(
	raw: Record<string, string> | null | undefined,
): TodoSnapshot | undefined {
	if (!raw) {
		return undefined;
	}
	try {
		const todos = Object.values(raw).map((value) => {
			const parsed = JSON.parse(value);
			return {
				id: parsed.id,
				content: parsed.content,
				status: parsed.status,
				priority: parsed.priority,
				active_form: parsed.activeForm ?? parsed.active_form,
			};
		});
		return { todos };
	} catch {
		return undefined;
	}
}

// ─── Module-level SSE deduplication ──────────────────────────────────────────

/**
 * Reference-equality dedup tracker. Since subscribeToTasksSSE fans the same
 * event object reference to every subscriber, we track the last ingested
 * reference to avoid writing to the store N times for N mounted subscribers.
 */
let lastIngestedSSEEvent: { event: ApiTaskSSEUpdate; eventType: ApiTaskSSEEventType } | null = null;

// ─── Store ────────────────────────────────────────────────────────────────────

type TasksActions = {
	// SSE lifecycle
	mountSSESubscriber: (
		getService: () => AssistanceService,
	) => (api: StoreActionApi<TasksStoreState>) => void;
	unmountSSESubscriber: () => (api: StoreActionApi<TasksStoreState>) => void;

	// Active conversation context
	setActiveConversationContext: (params: {
		conversationId: string | null;
		lastAgentMessageId: string | null;
		status: number | null;
	}) => (api: StoreActionApi<TasksStoreState>) => void;

	// SSE ingestion (extended to cover all event types + guards)
	ingestTasksSSEEvent: (
		event: ApiTaskSSEUpdate | ApiTaskSnapshotSSEUpdate | Record<string, unknown>,
		eventType: ApiTaskSSEEventType,
	) => (api: StoreActionApi<TasksStoreState>) => void;

	// Task mutations from stream (synchronous, keyed by taskId)
	createTaskFromStream: (params: {
		taskId: string;
		conversationId: string;
		title: string;
		updatedAt?: string;
		messageId?: string;
		currentMessageId?: string | null;
		createdAt?: string;
		startedAt?: string | null;
		completedAt?: string | null;
		executionSegments?: ExecutionSegment[] | null;
	}) => (api: StoreActionApi<TasksStoreState>) => void;

	setTaskProgressState: (params: {
		// Either taskId OR conversationId is required. If conversationId is given,
		// the store resolves the most recent task for that conversation.
		taskId?: string;
		conversationId?: string;
		progressState: TaskProgressState;
		title?: string;
		seekingCompletedAck?: boolean;
		messageId?: string;
		completionMessageId?: string;
		updatedAt?: string;
		currentMessageId?: string | null;
		createdAt?: string;
		startedAt?: string | null;
		completedAt?: string | null;
		executionSegments?: ExecutionSegment[] | null;
		// `null` explicitly clears the field; `undefined` leaves it unchanged.
		activeInvocationId?: string | null;
		currentStep?: ApiTaskSSEUpdate['current_step'];
		resultSummary?: string | null;
		errorMessage?: string | null;
	}) => (api: StoreActionApi<TasksStoreState>) => void;

	setTaskTodoSnapshot: (params: {
		taskId?: string;
		conversationId?: string;
		todoSnapshot: TodoSnapshot;
	}) => (api: StoreActionApi<TasksStoreState>) => void;

	ackTaskCompleted: (params: { taskId: string }) => (api: StoreActionApi<TasksStoreState>) => void;

	clearTask: (params: { taskId: string }) => (api: StoreActionApi<TasksStoreState>) => void;

	// REST merge
	mergeConversationChannelTasks: (
		remoteTasks: ConversationChannelTask[],
	) => (api: StoreActionApi<TasksStoreState>) => void;

	/**
	 * Reconciles the unified store with the authoritative REST snapshot.
	 * Accepts either the rich SSE shape (`ApiTaskSSEUpdate`) or the REST shape
	 * (`ApiTask`); the action only reads the structural fields common to both.
	 */
	mergeAllTasksSnapshot: (
		tasks: ApiTask[] | ApiTaskSSEUpdate[],
	) => (api: StoreActionApi<TasksStoreState>) => void;

	/**
	 * Merges one paginated REST page without removing tasks that are absent from
	 * that page. Use this for cursor pagination where a response is not the full
	 * authoritative task list.
	 */
	mergeAllTasksPage: (
		tasks: ApiTask[] | ApiTaskSSEUpdate[],
		nextCursor?: string | null,
	) => (api: StoreActionApi<TasksStoreState>) => void;

	// Optimistic mutations from UI
	removeTaskById: (taskId: string) => (api: StoreActionApi<TasksStoreState>) => void;
	markTaskCancelled: (taskId: string) => (api: StoreActionApi<TasksStoreState>) => void;
	setTaskTitle: (taskId: string, title: string) => (api: StoreActionApi<TasksStoreState>) => void;

	// Conversation cleanup
	removeTasksForConversation: (
		conversationId: string,
	) => (api: StoreActionApi<TasksStoreState>) => void;

	// Read-only accessors for non-React callers (mutations + reads share the actions surface)
	getActiveTaskForConversation: (
		conversationId: string,
	) => (api: StoreActionApi<TasksStoreState>) => UnifiedTask | undefined;

	getTaskById: (
		taskId: string,
	) => (api: StoreActionApi<TasksStoreState>) => UnifiedTask | undefined;

	// SSE connected flag (called internally by the SSE subscriber)
	_setSSEConnected: (connected: boolean) => (api: StoreActionApi<TasksStoreState>) => void;
};

const store = createStore<TasksStoreState, TasksActions>({
	name: 'tasks',
	initialState,
	actions: {
		// ─── SSE Lifecycle ────────────────────────────────────────────────────

		mountSSESubscriber:
			(getService) =>
			({ getState, setState }) => {
				const { sseSubscriberCount } = getState();
				const newCount = sseSubscriberCount + 1;
				setState({ sseSubscriberCount: newCount });

				if (newCount === 1) {
					// First subscriber — open SSE connection via existing ref-counted manager.
					// The module-level subscribeToTasksSSE handles tab-visibility and reconnect.
					const unsubscribe = subscribeToTasksSSE(
						getService,
						(event: ApiTaskSSEUpdate, eventType: ApiTaskSSEEventType) => {
							// Deduplicate store ingestion across multiple mounted subscribers
							if (lastIngestedSSEEvent?.event !== event) {
								defaultRegistry.getStore(store).actions.ingestTasksSSEEvent(event, eventType);
								lastIngestedSSEEvent = { event, eventType };
							}
						},
					);
					// Store the unsubscribe fn at module level so unmountSSESubscriber can call it
					sseUnsubscribe = unsubscribe;
				}
			},

		unmountSSESubscriber:
			() =>
			({ getState, setState }) => {
				const { sseSubscriberCount } = getState();
				const newCount = Math.max(0, sseSubscriberCount - 1);
				setState({ sseSubscriberCount: newCount });

				if (newCount === 0 && sseUnsubscribe) {
					sseUnsubscribe();
					sseUnsubscribe = null;
					lastIngestedSSEEvent = null;
				}
			},

		_setSSEConnected:
			(connected) =>
			({ setState }) => {
				setState({ sseConnected: connected });
			},

		// ─── Active Conversation Context ──────────────────────────────────────

		setActiveConversationContext:
			({ conversationId, lastAgentMessageId, status }) =>
			({ setState }) => {
				setState({
					activeConversationId: conversationId,
					activeConversationLastAgentMessageId: lastAgentMessageId,
					activeConversationStatus: status,
				});
			},

		// ─── SSE Ingestion ────────────────────────────────────────────────────

		ingestTasksSSEEvent:
			(event, eventType) =>
			({ getState, setState }) => {
				const state = getState();

				if (eventType === 'connected') {
					setState({ sseConnected: true });
					return;
				}

				if (eventType === 'task_snapshot') {
					const snapshot = event as ApiTaskSnapshotSSEUpdate;
					const tasks = snapshot.tasks ?? [];
					if (tasks.length === 0) {
						return;
					}
					const next = new Map(state.tasksByTaskId);
					let changed = false;
					for (const incoming of tasks) {
						const existing = next.get(incoming.task_id);
						if (!shouldOverwrite(existing, incoming.updated_at, incoming.status)) {
							continue;
						}
						next.set(incoming.task_id, mergeSSEUpdateIntoTask(existing, incoming));
						changed = true;
					}
					if (changed) {
						setState({ tasksByTaskId: next });
					}
					return;
				}

				if (eventType === 'task_status') {
					const update = event as ApiTaskSSEUpdate;
					const conversationId = update.conversation_id ?? null;

					// Guard A: reject if the owning conversation is actively streaming
					// (CHAT_STORE_STATUS.idle = 0; non-idle means streaming/loading)
					if (
						conversationId &&
						conversationId === state.activeConversationId &&
						state.activeConversationStatus !== null &&
						state.activeConversationStatus !== 0 // CHAT_STORE_STATUS.idle
					) {
						return;
					}

					// Guard B: reject stale SSE reconnect replays by currentMessageId mismatch
					if (
						conversationId &&
						conversationId === state.activeConversationId &&
						update.current_message_id &&
						state.activeConversationLastAgentMessageId &&
						update.current_message_id !== state.activeConversationLastAgentMessageId
					) {
						return;
					}

					const existing = state.tasksByTaskId.get(update.task_id);
					const progressState = mapApiTaskStatusToProgressState(update.status);

					// Skip if already applied (same state from stream)
					if (
						existing &&
						existing.progressState === progressState &&
						existing.currentMessageId === (update.current_message_id ?? null) &&
						existing.updatedAt >= update.updated_at
					) {
						return;
					}

					if (!shouldOverwrite(existing, update.updated_at, update.status)) {
						return;
					}

					const next = new Map(state.tasksByTaskId);
					if (!existing) {
						// Create-if-missing: task arrived via SSE before stream
						next.set(update.task_id, mergeSSEUpdateIntoTask(undefined, update));
					} else {
						next.set(update.task_id, mergeSSEUpdateIntoTask(existing, update));
					}
					setState({ tasksByTaskId: next });
					return;
				}

				if (eventType === 'task_todo_update') {
					const update = event as ApiTaskSSEUpdate;
					const conversationId = update.conversation_id ?? null;

					// Guard A (parity with task_status): reject if the owning conversation
					// is actively streaming. The in-flight stream will deliver a fresher
					// todo snapshot momentarily.
					if (
						conversationId &&
						conversationId === state.activeConversationId &&
						state.activeConversationStatus !== null &&
						state.activeConversationStatus !== 0 // CHAT_STORE_STATUS.idle
					) {
						return;
					}

					// Guard B (parity with task_status): reject stale SSE reconnect
					// replays by currentMessageId mismatch.
					if (
						conversationId &&
						conversationId === state.activeConversationId &&
						update.current_message_id &&
						state.activeConversationLastAgentMessageId &&
						update.current_message_id !== state.activeConversationLastAgentMessageId
					) {
						return;
					}

					const existing = state.tasksByTaskId.get(update.task_id);
					if (!existing || isTerminalProgressState(existing.progressState)) {
						return;
					}
					const todoSnapshot = parseTodoList(update.todo_list ?? undefined);
					if (!todoSnapshot) {
						return;
					}
					const next = new Map(state.tasksByTaskId);
					next.set(update.task_id, { ...existing, todoSnapshot });
					setState({ tasksByTaskId: next });
				}
			},

		// ─── Task Mutations from Stream ───────────────────────────────────────

		createTaskFromStream:
			({
				taskId,
				conversationId,
				title,
				updatedAt,
				messageId,
				currentMessageId,
				createdAt,
				startedAt,
				completedAt,
				executionSegments,
			}) =>
			({ getState, setState }) => {
				const { tasksByTaskId } = getState();

				// Idempotency guard — skip if task already exists
				if (tasksByTaskId.has(taskId)) {
					return;
				}

				const newTask: UnifiedTask = {
					taskId,
					conversationId,
					agentId: null,
					triggerType: null,
					progressState: 'Draft',
					rawStatus: 'DRAFT',
					activeInvocationId: null,
					title,
					plan: null,
					resultSummary: null,
					errorMessage: null,
					todoSnapshot: undefined,
					currentStep: null,
					executionSegments: executionSegments ?? null,
					messageId: messageId ?? null,
					completionMessageId: null,
					currentMessageId: currentMessageId ?? null,
					seekingCompletedAck: false,
					createdAt: createdAt ?? null,
					startedAt: startedAt ?? null,
					// Prefer the supplied updatedAt, then fall back to createdAt so the
					// initial monotonic comparison in `shouldOverwrite` doesn't reject a
					// real BE update that arrives with an older timestamp than `Date.now()`.
					updatedAt: updatedAt ?? createdAt ?? new Date().toISOString(),
					completedAt: completedAt ?? null,
				};

				const next = new Map(tasksByTaskId);
				next.set(taskId, newTask);
				setState({ tasksByTaskId: next });
			},

		setTaskProgressState:
			({
				taskId,
				conversationId,
				progressState,
				title,
				seekingCompletedAck,
				messageId,
				completionMessageId,
				updatedAt,
				currentMessageId,
				createdAt,
				startedAt,
				completedAt,
				executionSegments,
				activeInvocationId,
				currentStep,
				resultSummary,
				errorMessage,
			}) =>
			({ getState, setState }) => {
				const { tasksByTaskId } = getState();
				const resolvedTaskId =
					taskId ??
					(conversationId
						? findActiveTaskIdForConversation(tasksByTaskId, conversationId)
						: undefined);

				// Auto-create when there is no active task yet for the conversation.
				// Allows the chat-store to drive optimistic state changes
				// (e.g. AwaitingPlanConfirmation) before
				// the BE has issued a real task_id.
				if (!resolvedTaskId) {
					if (!conversationId) {
						return;
					}
					const localTaskId = `task-${Date.now()}`;
					const newTask: UnifiedTask = {
						taskId: localTaskId,
						conversationId,
						agentId: null,
						triggerType: null,
						progressState,
						rawStatus: progressStateToApiStatus(progressState),
						activeInvocationId: activeInvocationId ?? null,
						title: title ?? '',
						plan: null,
						resultSummary: resultSummary ?? null,
						errorMessage: errorMessage ?? null,
						todoSnapshot: undefined,
						currentStep: currentStep ?? null,
						executionSegments: executionSegments ?? null,
						messageId: messageId ?? null,
						completionMessageId: completionMessageId ?? null,
						currentMessageId: currentMessageId ?? null,
						seekingCompletedAck: seekingCompletedAck ?? false,
						createdAt: createdAt ?? new Date().toISOString(),
						startedAt: startedAt ?? null,
						updatedAt: updatedAt ?? createdAt ?? new Date().toISOString(),
						completedAt: completedAt ?? null,
					};
					const next = new Map(tasksByTaskId);
					next.set(localTaskId, newTask);
					setState({ tasksByTaskId: next });
					return;
				}
				const existing = tasksByTaskId.get(resolvedTaskId);

				if (!existing) {
					return;
				}

				// Terminal state protection
				if (isTerminalProgressState(existing.progressState)) {
					return;
				}

				const merged: UnifiedTask = {
					...existing,
					progressState,
					rawStatus: progressStateToApiStatus(progressState),
					// `null` is meaningful — explicit clear; `undefined` leaves field unchanged.
					...(activeInvocationId !== undefined ? { activeInvocationId } : {}),
					...(title !== undefined ? { title } : {}),
					...(seekingCompletedAck !== undefined ? { seekingCompletedAck } : {}),
					...(messageId !== undefined ? { messageId } : {}),
					...(completionMessageId !== undefined ? { completionMessageId } : {}),
					...(updatedAt !== undefined ? { updatedAt } : {}),
					...(currentMessageId !== undefined ? { currentMessageId } : {}),
					...(createdAt !== undefined ? { createdAt } : {}),
					...(startedAt !== undefined ? { startedAt } : {}),
					...(completedAt !== undefined ? { completedAt } : {}),
					...(executionSegments !== undefined
						? {
								executionSegments: mergeExecutionSegments(
									existing.executionSegments,
									executionSegments,
								),
							}
						: {}),
					// `null` is meaningful — explicit clear; `undefined` leaves field unchanged.
					...(currentStep !== undefined ? { currentStep } : {}),
					...(resultSummary !== undefined ? { resultSummary } : {}),
					...(errorMessage !== undefined ? { errorMessage } : {}),
				};

				const next = new Map(tasksByTaskId);
				next.set(resolvedTaskId, merged);
				setState({ tasksByTaskId: next });
			},

		setTaskTodoSnapshot:
			({ taskId, conversationId, todoSnapshot }) =>
			({ getState, setState }) => {
				const { tasksByTaskId } = getState();
				const resolvedTaskId =
					taskId ??
					(conversationId
						? findActiveTaskIdForConversation(tasksByTaskId, conversationId)
						: undefined);
				if (!resolvedTaskId) {
					return;
				}
				const existing = tasksByTaskId.get(resolvedTaskId);

				if (!existing || isTerminalProgressState(existing.progressState)) {
					return;
				}

				const next = new Map(tasksByTaskId);
				next.set(resolvedTaskId, { ...existing, todoSnapshot });
				setState({ tasksByTaskId: next });
			},

		ackTaskCompleted:
			({ taskId }) =>
			({ getState, setState }) => {
				const { tasksByTaskId } = getState();
				const existing = tasksByTaskId.get(taskId);
				if (!existing) {
					return;
				}
				const next = new Map(tasksByTaskId);
				next.set(taskId, { ...existing, seekingCompletedAck: false });
				setState({ tasksByTaskId: next });
			},

		clearTask:
			({ taskId }) =>
			({ getState, setState }) => {
				const { tasksByTaskId } = getState();
				if (!tasksByTaskId.has(taskId)) {
					return;
				}
				const next = new Map(tasksByTaskId);
				next.delete(taskId);
				setState({ tasksByTaskId: next });
			},

		// ─── REST Merge ───────────────────────────────────────────────────────

		mergeConversationChannelTasks:
			(remoteTasks) =>
			({ getState, setState }) => {
				if (!remoteTasks || remoteTasks.length === 0) {
					return;
				}

				// Sort ascending by created_at (oldest first, newest last = active)
				const sorted = [...remoteTasks].sort(
					(a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
				);

				const { tasksByTaskId } = getState();
				const next = new Map(tasksByTaskId);
				let changed = false;

				for (const remote of sorted) {
					const existing = next.get(remote.task_id);
					const merged = mergeChannelTaskIntoUnified(existing, remote);
					next.set(remote.task_id, merged);
					changed = true;
				}

				if (changed) {
					setState({ tasksByTaskId: next });
				}
			},

		mergeAllTasksSnapshot:
			(tasks) =>
			({ getState, setState }) => {
				if (!tasks) {
					return;
				}

				const { tasksByTaskId } = getState();
				const next = new Map(tasksByTaskId);
				let changed = false;

				// 1. Upsert all tasks from the snapshot (REST is authoritative for tasks it returns).
				const snapshotIds = new Set<string>();
				for (const incoming of tasks) {
					snapshotIds.add(incoming.task_id);
					const existing = next.get(incoming.task_id);
					if (!shouldOverwrite(existing, incoming.updated_at, incoming.status)) {
						continue;
					}
					next.set(incoming.task_id, mergeSSEUpdateIntoTask(existing, incoming));
					changed = true;
				}

				// 2. Reconcile: remove any task absent from the REST snapshot **only** when
				//    it's already in a terminal state. Non-terminal (in-flight) tasks may
				//    legitimately be present locally but not yet visible in REST (race), so
				//    we preserve them and rely on SSE to deliver their final state.
				for (const [taskId, task] of tasksByTaskId) {
					if (snapshotIds.has(taskId)) {
						continue;
					}
					if (isTerminalProgressState(task.progressState)) {
						next.delete(taskId);
						changed = true;
					}
				}

				if (changed) {
					setState({ tasksByTaskId: next });
				}
			},

		mergeAllTasksPage:
			(tasks, nextCursor) =>
			({ getState, setState }) => {
				const { tasksByTaskId } = getState();
				const next = new Map(tasksByTaskId);
				let changed = false;

				for (const incoming of tasks) {
					const existing = next.get(incoming.task_id);
					if (!shouldOverwrite(existing, incoming.updated_at, incoming.status)) {
						continue;
					}
					next.set(incoming.task_id, mergeSSEUpdateIntoTask(existing, incoming));
					changed = true;
				}

				if (changed || nextCursor !== undefined) {
					setState({
						...(changed ? { tasksByTaskId: next } : {}),
						...(nextCursor !== undefined
							? { allTasksNextCursor: nextCursor, hasLoadedAllTasksPage: true }
							: {}),
					});
				}
			},

		// ─── Optimistic mutations from UI (tasks-list, etc.) ─────────────────

		/**
		 * Removes a single task by taskId. Used for optimistic delete from
		 * the tasks-list screen before the API call resolves.
		 */
		removeTaskById:
			(taskId: string) =>
			({ getState, setState }) => {
				const { tasksByTaskId } = getState();
				if (!tasksByTaskId.has(taskId)) {
					return;
				}
				const next = new Map(tasksByTaskId);
				next.delete(taskId);
				setState({ tasksByTaskId: next });
			},

		/**
		 * Optimistically marks a task as Cancelled (terminal state). Used by the
		 * tasks-list stop action before the API call resolves.
		 */
		markTaskCancelled:
			(taskId: string) =>
			({ getState, setState }) => {
				const { tasksByTaskId } = getState();
				const existing = tasksByTaskId.get(taskId);
				if (!existing) {
					return;
				}
				const now = new Date().toISOString();
				const next = new Map(tasksByTaskId);
				next.set(taskId, {
					...existing,
					progressState: 'Cancelled',
					rawStatus: 'CANCELLED',
					updatedAt: now,
					completedAt: existing.completedAt ?? now,
				});
				setState({ tasksByTaskId: next });
			},

		/**
		 * Optimistically updates a task's title. Used by the tasks-list rename
		 * action before the API call resolves.
		 */
		setTaskTitle:
			(taskId: string, title: string) =>
			({ getState, setState }) => {
				const { tasksByTaskId } = getState();
				const existing = tasksByTaskId.get(taskId);
				if (!existing || existing.title === title) {
					return;
				}
				const next = new Map(tasksByTaskId);
				next.set(taskId, {
					...existing,
					title,
					updatedAt: new Date().toISOString(),
				});
				setState({ tasksByTaskId: next });
			},

		// ─── Conversation cleanup ─────────────────────────────────────────────

		removeTasksForConversation:
			(conversationId) =>
			({ getState, setState }) => {
				const { tasksByTaskId } = getState();
				const next = new Map(tasksByTaskId);
				let changed = false;
				for (const [taskId, task] of tasksByTaskId) {
					if (task.conversationId === conversationId) {
						next.delete(taskId);
						changed = true;
					}
				}
				if (changed) {
					setState({ tasksByTaskId: next });
				}
			},

		// Read-only accessor — used by chat-store actions that need the latest
		// active task synchronously without going through React.
		getActiveTaskForConversation:
			(conversationId) =>
			({ getState }) => {
				return findActiveTaskForConversation(getState().tasksByTaskId, conversationId);
			},

		// Read-only accessor — single-task lookup by id. Same imperative shape as
		// `getActiveTaskForConversation` so chat-store callers don't need any
		// React state plumbing.
		getTaskById:
			(taskId) =>
			({ getState }) => {
				return getState().tasksByTaskId.get(taskId);
			},
	},
});

// ─── Module-level SSE unsubscribe reference ───────────────────────────────────
// Stored outside the store state (not serializable, not reactive).
let sseUnsubscribe: (() => void) | null = null;

/**
 * Finds the active task for a conversation.
 * Prefers the most recent **non-terminal** task; falls back to the most recent
 * task overall if all tasks for the conversation are in terminal state.
 *
 * "Active" semantics: a conversation has at most one in-flight task at any
 * point in time. Older tasks (terminal) remain associated with the conversation
 * for history but should not be treated as the current active task.
 */
/**
 * Internal selector helper. Use the `useActiveTaskForConversation` /
 * `useActiveTasksForConversations` hooks (React) or the
 * `getActiveTaskForConversation` action (non-React) instead.
 */
function findActiveTaskForConversation(
	tasksByTaskId: Map<string, UnifiedTask>,
	conversationId: string,
): UnifiedTask | undefined {
	let latestNonTerminal: UnifiedTask | undefined;
	let latestAny: UnifiedTask | undefined;
	for (const task of tasksByTaskId.values()) {
		if (task.conversationId !== conversationId) {
			continue;
		}
		if (!latestAny || (task.createdAt ?? '') > (latestAny.createdAt ?? '')) {
			latestAny = task;
		}
		if (!isTerminalProgressState(task.progressState)) {
			if (!latestNonTerminal || (task.createdAt ?? '') > (latestNonTerminal.createdAt ?? '')) {
				latestNonTerminal = task;
			}
		}
	}
	return latestNonTerminal ?? latestAny;
}

function findActiveTaskIdForConversation(
	tasksByTaskId: Map<string, UnifiedTask>,
	conversationId: string,
): string | undefined {
	return findActiveTaskForConversation(tasksByTaskId, conversationId)?.taskId;
}

// ─── Internal merge helpers ───────────────────────────────────────────────────

function progressStateToApiStatus(state: TaskProgressState): ApiTaskStatus {
	const map: Record<TaskProgressState, ApiTaskStatus> = {
		Draft: 'DRAFT',
		Queued: 'QUEUED',
		Running: 'RUNNING',
		AwaitingDisambiguation: 'AWAITING_DISAMBIGUATION',
		AwaitingPlanConfirmation: 'AWAITING_PLAN_CONFIRMATION',
		AwaitingActionConfirmation: 'AWAITING_ACTION_CONFIRMATION',
		Completed: 'COMPLETED',
		Failed: 'FAILED',
		Cancelled: 'CANCELLED',
	};
	return map[state];
}

function mergeSSEUpdateIntoTask(
	existing: UnifiedTask | undefined,
	incoming: ApiTaskSSEUpdate | ApiTask,
): UnifiedTask {
	const progressState = mapApiTaskStatusToProgressState(incoming.status);
	// `current_step` only exists on the SSE shape; REST snapshots don't carry it.
	const incomingCurrentStep =
		'current_step' in incoming ? (incoming.current_step ?? null) : (existing?.currentStep ?? null);
	// `agent_id` and `trigger_type` are on the REST shape but not on every SSE update.
	const incomingAgentId = 'agent_id' in incoming ? incoming.agent_id : (existing?.agentId ?? null);
	const incomingTriggerType =
		'trigger_type' in incoming
			? (incoming.trigger_type as 'EXPLICIT' | 'IMPLICIT' | null)
			: (existing?.triggerType ?? null);
	const incomingPlan = 'plan' in incoming ? (incoming.plan ?? null) : (existing?.plan ?? null);
	const incomingCompletedAt =
		'completed_at' in incoming
			? (incoming.completed_at ?? existing?.completedAt ?? null)
			: (existing?.completedAt ?? null);
	return {
		taskId: incoming.task_id,
		conversationId: incoming.conversation_id ?? existing?.conversationId ?? null,
		agentId: incomingAgentId,
		triggerType: incomingTriggerType,
		progressState,
		rawStatus: incoming.status,
		// Not carried on the SSE/REST wire shape. Preserve the local value only while
		// the task stays in the same invocation-bearing awaiting state; otherwise clear
		// it so a dead invocation id can't outlive its prompt.
		activeInvocationId: shouldPreserveActiveInvocationId(existing, progressState)
			? (existing?.activeInvocationId ?? null)
			: null,
		title: incoming.title,
		plan: incomingPlan,
		resultSummary: incoming.result_summary,
		errorMessage: incoming.error_message,
		todoSnapshot: existing?.todoSnapshot,
		currentStep: incomingCurrentStep,
		executionSegments: parseExecutionSegments(incoming.execution_segments ?? undefined),
		messageId: incoming.message_id ?? existing?.messageId ?? null,
		completionMessageId: incoming.completion_message_id ?? existing?.completionMessageId ?? null,
		currentMessageId: incoming.current_message_id ?? existing?.currentMessageId ?? null,
		// Local-only UI state: preserve from existing
		seekingCompletedAck: existing?.seekingCompletedAck ?? false,
		createdAt: incoming.created_at ?? existing?.createdAt ?? null,
		startedAt: incoming.started_at ?? existing?.startedAt ?? null,
		updatedAt: incoming.updated_at,
		completedAt: incomingCompletedAt,
	};
}

function mergeChannelTaskIntoUnified(
	existing: UnifiedTask | undefined,
	remote: ConversationChannelTask,
): UnifiedTask {
	const progressState = mapApiTaskStatusToProgressState(remote.status);
	const remoteExecutionSegments = parseExecutionSegments(remote.execution_segments ?? undefined);
	const remoteTodoSnapshot = parseTodoList(remote.todo_list ?? undefined);

	// Local wins for progressState and todoSnapshot when local is strictly newer
	const localIsNewer = existing?.updatedAt && existing.updatedAt > remote.updated_at;
	const resolvedProgressState = localIsNewer
		? (existing?.progressState ?? progressState)
		: progressState;

	return {
		taskId: remote.task_id,
		conversationId: remote.conversation_id,
		agentId: remote.agent_id,
		triggerType: remote.trigger_type,
		progressState: resolvedProgressState,
		rawStatus: localIsNewer ? (existing?.rawStatus ?? remote.status) : remote.status,
		// Not carried on the channel wire shape. Preserve the local value only while
		// the task stays in the same invocation-bearing awaiting state (compared
		// against the FINAL resolved progressState, which accounts for localIsNewer);
		// otherwise clear it so a dead invocation id can't outlive its prompt.
		activeInvocationId: shouldPreserveActiveInvocationId(existing, resolvedProgressState)
			? (existing?.activeInvocationId ?? null)
			: null,
		title: remote.title,
		plan: remote.plan,
		resultSummary: remote.result_summary,
		errorMessage: remote.error_message,
		todoSnapshot: localIsNewer
			? existing?.todoSnapshot
			: (remoteTodoSnapshot ?? existing?.todoSnapshot),
		currentStep: existing?.currentStep ?? null,
		executionSegments: mergeExecutionSegments(existing?.executionSegments, remoteExecutionSegments),
		messageId: remote.message_id,
		completionMessageId: remote.completion_message_id,
		currentMessageId: remote.current_message_id,
		// Local-only: seekingCompletedAck NEVER overwritten by REST
		seekingCompletedAck: existing?.seekingCompletedAck ?? false,
		createdAt: remote.created_at,
		startedAt: remote.started_at,
		updatedAt: remote.updated_at,
		completedAt: remote.completed_at,
	};
}

// ─── Bound actions type ───────────────────────────────────────────────────────

type TasksBoundActions = BoundActions<TasksStoreState, TasksActions>;

// ─── Hooks ────────────────────────────────────────────────────────────────────

export const useTasksStore: HookFunction<TasksStoreState, TasksBoundActions, void> =
	createHook(store);

export const useTasksStoreActions: HookActionsFunction<TasksBoundActions> =
	createActionsHook(store);

/**
 * Returns the active task for a given conversationId, reactively updated.
 * Prefers the most recent **non-terminal** task; falls back to the most recent
 * task overall if all tasks for the conversation are in terminal state.
 */
export const useActiveTaskForConversation: HookFunction<
	UnifiedTask | undefined,
	TasksBoundActions,
	string
> = createHook(store, {
	selector: (state: TasksStoreState, conversationId: string): UnifiedTask | undefined => {
		if (!conversationId) {
			return undefined;
		}
		return findActiveTaskForConversation(state.tasksByTaskId, conversationId);
	},
});

/** Returns all tasks as an array, reactively updated. Used by the tasks-list screen. */
export const useAllTasks: HookFunction<UnifiedTask[], TasksBoundActions, void> = createHook(store, {
	selector: (state: TasksStoreState): UnifiedTask[] => Array.from(state.tasksByTaskId.values()),
});

/**
 * Returns the current task notices, newest first.
 *
 * All notice consumers — inbox list, menu badge, notifications — read from this one derived
 * model, so they cannot disagree. See `./selectTaskNotices` for the reason and ordering contract.
 */
export const useTaskNotices: HookFunction<TaskNotice[], TasksBoundActions, void> = createHook(
	store,
	{
		selector: (state: TasksStoreState): TaskNotice[] =>
			selectTaskNotices(state.tasksByTaskId.values()),
	},
);

/** Returns persisted all-tasks pagination state. Used to resume pagination after remounts. */
export const useAllTasksPaginationState: HookFunction<
	Pick<TasksStoreState, 'allTasksNextCursor' | 'hasLoadedAllTasksPage'>,
	TasksBoundActions,
	void
> = createHook(store, {
	selector: (state: TasksStoreState) => ({
		allTasksNextCursor: state.allTasksNextCursor,
		hasLoadedAllTasksPage: state.hasLoadedAllTasksPage,
	}),
});

/**
 * Returns active tasks for a list of conversationIds as a Map<convId, UnifiedTask | undefined>.
 * Used by list-rendering consumers (rovo-navigation, conversation-list-refresh) so they can
 * derive each row's active task with a single hook call instead of useAllTasks + useMemo
 * Map-rebuild + per-row findActiveTaskForConversation.
 *
 * Re-renders whenever any task in the store changes (selector returns a new Map
 * each time). Pass a stable
 * `conversationIds` reference (e.g. via useMemo) to avoid recomputing the Map
 * unnecessarily on every parent render.
 */
export const useActiveTasksForConversations: HookFunction<
	Map<string, UnifiedTask | undefined>,
	TasksBoundActions,
	readonly string[]
> = createHook(store, {
	selector: (
		state: TasksStoreState,
		conversationIds: readonly string[],
	): Map<string, UnifiedTask | undefined> => {
		const result = new Map<string, UnifiedTask | undefined>();
		for (const id of conversationIds) {
			result.set(id, findActiveTaskForConversation(state.tasksByTaskId, id));
		}
		return result;
	},
});

/** Returns a specific task by taskId. */
export const useTaskById: HookFunction<UnifiedTask | undefined, TasksBoundActions, string> =
	createHook(store, {
		selector: (state: TasksStoreState, taskId: string): UnifiedTask | undefined =>
			state.tasksByTaskId.get(taskId),
	});

/** Returns whether the SSE connection is established. */
export const useSSEConnected: HookFunction<boolean, TasksBoundActions, void> = createHook(store, {
	selector: (state: TasksStoreState): boolean => state.sseConnected,
});

// ─── Standalone utilities ─────────────────────────────────────────────────────

// All imperative accessors intentionally NOT exported. The chat-store accesses
// unified tasks actions through container deps (ChatStoreContainerProps.unifiedTasksActions);
// React hook callers use `useTasksStoreActions()` to get the same actions through
// sweet-state. This keeps the dependency injection pattern consistent.

/**
 * Adapter that converts a `UnifiedTask` (camelCase, internal) into the
 * `ApiTaskSSEUpdate` shape (snake_case, public contract) used by
 * `WithLiveTask.currentTask`. Pure function with no side effects.
 *
 * Used by consumers (rovo-navigation, conversation-list-refresh) so the public
 * conversation contract stays identical while tasks are sourced from the unified store.
 */
export const unifiedTaskToApiSSE = (task: UnifiedTask): ApiTaskSSEUpdate => ({
	task_id: task.taskId,
	title: task.title,
	status: task.rawStatus,
	current_step: task.currentStep,
	result_summary: task.resultSummary,
	error_message: task.errorMessage,
	conversation_id: task.conversationId,
	message_id: task.messageId,
	completion_message_id: task.completionMessageId,
	current_message_id: task.currentMessageId,
	created_at: task.createdAt,
	started_at: task.startedAt,
	updated_at: task.updatedAt,
	// Preserve raw shape for execution_segments and todo_list (Record<string,string>).
	// We do not have the raw representation any more after parsing, so leave as null.
	execution_segments: null,
	todo_list: null,
});
